import { useCallback, useEffect, useState } from "react";
import {
  GameClubImage,
  GameClubImageSelectItem,
  GameClubImageSelectWrapper,
  GameClubImageUpload,
  GameClubJoinContent,
  GameClubJoinContentWrapper,
  GameClubJoinHeader,
  GameClubJoinName,
  GameClubJoinWarning,
  GameClubJoinWrapper,
  GameClubOverlay,
} from "../Style/GameClubAllStyles";
import { useGameClubContext } from "../Hook/GameClubContext";
import {
  createClub,
  editClub,
  getClubDetail,
  joinClub,
} from "../../../../api/game-club";
import { useGameContext } from "../Hook/GameContext";
import { enqueueSnackbar } from "notistack";
import { getUserTicketInfo } from "../../../../api/game";
import { LOCAL_STORAGE_ACCESS_KEY } from "../../../../utils/network";
import { checkAlreadyRewarded } from "../../../../api/club-reward";

const JoinWarningText = [
  {
    text: "클럽은 한 번 가입 후 탈퇴하거나 변경할 수 없습니다.",
    isRead: false,
  },
];

const OpenWarningText = [
  {
    text: "클럽 개설시 10,000,000 GP가 필요합니다.",
    isRead: false,
  },
  {
    text: "한 번 개설한 클럽은 폐쇄가 불가능합니다.",
    isRead: false,
  },
];

export interface GameClubJoinProps {
  onClose: () => void;
  type: string;
}
export const GameClubJoin = ({ onClose, type }: GameClubJoinProps) => {
  const { user, getUserTicket, openDialog } = useGameContext();
  const [clubName, setClubName] = useState<string>("");
  const [clubInfo, setClubInfo] = useState<string>("");
  const [imageFileUpload, setImageFileUpload] = useState<File>();
  const [imageUrl, setImageUrl] = useState<string | null>("");
  const {
    selectClubId,
    setClubId,
    setClubUserId,
    setResetUserData,
    resetUserData,
  } = useGameClubContext();
  const [selectedProfileType, setSelectedProfileType] = useState<number | null>(
    null
  );
  const [isSelectProfile, setIsSelectProfile] = useState<boolean>(false);
  const [warningText, setWarningText] = useState<
    { text: string; isRead: boolean }[]
  >([]);

  useEffect(() => {
    if (type === "join") {
      setWarningText(JoinWarningText);
    }
    if (type === "opened") {
      setWarningText(OpenWarningText);
    }
  }, [type]);

  const getClubData = async () => {
    if (selectClubId === -1) return;
    const token = localStorage.getItem(LOCAL_STORAGE_ACCESS_KEY);
    const accessToken = token ? token.replace("Bearer ", "") : "-";
    const res = await getClubDetail(selectClubId, accessToken);

    setClubName(res.payload.name);
    setClubInfo(res.payload.description);
    setImageUrl(res.payload.imageUrl);
    setSelectedProfileType(res.payload.profileType);
  };

  useEffect(() => {
    getClubData();
  }, []);

  const handleJoin = async () => {
    if (!user) return;
    if (warningText.some((item) => !item.isRead)) {
      enqueueSnackbar("가입 전 안내사항을 읽고 동의해주세요.", {
        variant: "error",
      });
      return;
    }
    const token = localStorage.getItem(LOCAL_STORAGE_ACCESS_KEY);
    const accessToken = token ? token.replace("Bearer ", "") : "-";
    try {
      await joinClub({ clubId: selectClubId, token: accessToken });
      setResetUserData(!resetUserData);
      setClubId(selectClubId);
      setClubUserId(user.id);
      await checkAlreadyRewarded(selectClubId, user.id);
      openDialog({
        text: `클럽 가입 리워드 \n 100,000GP를 받으셨어요👏`,
        buttonText: "확인",
        onConfirm: () => {
          getUserTicket();
        },
      });
      onClose();
    } catch (e: any) {
      if (Number(e.statusCode) === 409) {
        enqueueSnackbar("이미 가입 신청한 클럽이 있습니다.", {
          variant: "error",
        });
        setResetUserData(!resetUserData);
        setClubId(selectClubId);
        setClubUserId(user.id);
        onClose();
        return;
      }
      console.log(e);
    }
  };

  const getMyGp = async () => {
    if (!user) return null;
    const res = await getUserTicketInfo({ userId: user.id });
    return res.data.user.point;
  };

  const handleCreate = async () => {
    if (warningText.some((item) => !item.isRead)) {
      enqueueSnackbar("개설 전 안내사항을 읽고 동의해주세요.", {
        variant: "error",
      });
      return;
    }
    if (clubName.trim().length === 0) {
      enqueueSnackbar("클럽 이름을 입력해주세요.", {
        variant: "error",
      });
      return;
    }
    if (clubInfo.trim().length === 0) {
      enqueueSnackbar("클럽 설명을 입력해주세요.", {
        variant: "error",
      });
      return;
    }
    const myGp = await getMyGp();

    if (!myGp || myGp < 1000000) {
      enqueueSnackbar("클럽 개설 GP가 부족합니다.", {
        variant: "error",
      });
      return;
    }

    const request = new FormData();
    request.append("name", clubName);
    request.append("description", clubInfo);
    const token = localStorage.getItem(LOCAL_STORAGE_ACCESS_KEY);
    const accessToken = token ? token.replace("Bearer ", "") : "-";
    request.append("token", accessToken);
    try {
      await createClub(request);
      onClose();
      openDialog({
        text: `클럽 개설 신청이 \n 완료 되었습니다. `,
        buttonText: "확인",
        onConfirm: () => {},
      });
      setResetUserData(!resetUserData);
      getUserTicket();
    } catch (e: any) {
      if (Number(e.code) === 200) {
        onClose();
        openDialog({
          text: `클럽 개설 신청이 \n 완료 되었습니다. `,
          buttonText: "확인",
          onConfirm: () => {},
        });
        setResetUserData(!resetUserData);
        return;
      }
      console.log(e);
    }
  };

  const handleEdit = async () => {
    if (clubName.trim().length === 0) {
      enqueueSnackbar("클럽 이름을 입력해주세요.", {
        variant: "error",
      });
      return;
    }
    if (clubInfo.trim().length === 0) {
      enqueueSnackbar("클럽 설명을 입력해주세요.", {
        variant: "error",
      });
      return;
    }
    if (selectedProfileType === null) {
      enqueueSnackbar("프로필 이미지를 선택해주세요.", {
        variant: "error",
      });
      return;
    }
    const request = new FormData();
    request.append("name", clubName);
    request.append("description", clubInfo);
    request.append("profileType", selectedProfileType.toString());
    const token = localStorage.getItem(LOCAL_STORAGE_ACCESS_KEY);
    const accessToken = token ? token.replace("Bearer ", "") : "-";
    request.append("token", accessToken);
    try {
      await editClub(request, selectClubId);
      onClose();
      openDialog({
        text: `클럽 정보가 \n 변경 되었습니다. `,
        buttonText: "확인",
        onConfirm: () => {},
      });
      setResetUserData(!resetUserData);
    } catch (e: any) {
      if (Number(e.code) === 200) {
        onClose();
        openDialog({
          text: `클럽 정보가 \n 변경 되었습니다. `,
          buttonText: "확인",
          onConfirm: () => {},
        });
        setResetUserData(!resetUserData);
        return;
      }
      console.log(e);
    }
  };

  const getTitleText = () => {
    switch (type) {
      case "join":
        return "클럽가입";
      case "opened":
        return "클럽개설";
      case "read":
        return "클럽 정보";
      case "edit":
        return "클럽 정보 수정";
    }
  };

  const getButtonText = () => {
    switch (type) {
      case "join":
        return "가입하기";
      case "opened":
        return "클럽 개설";
      case "edit":
        return "정보변경";
    }
  };

  return (
    <>
      <GameClubJoinWrapper>
        <GameClubJoinContentWrapper>
          <GameClubJoinHeader>
            <div className="title">{getTitleText()}</div>
            <img
              src="/image-web/game/club/close.svg"
              alt="close"
              onClick={() => onClose()}
            />
          </GameClubJoinHeader>
          <GameClubJoinContent>
            <div className="club-box">
              <GameClubImageUpload>
                <GameClubImage
                  onClick={() => {
                    if (type === "edit") setIsSelectProfile(!isSelectProfile);
                  }}
                >
                  {selectedProfileType !== null && (
                    <img
                      src={`/image-web/game/club/profile_${selectedProfileType}.png`}
                      className={`profile-image ${
                        type === "read" || type === "join" ? "read" : ""
                      }`}
                      alt="프로필 이미지"
                    />
                  )}
                  {type === "edit" && (
                    <span className="info">
                      클럽 마크 <br />
                      변경
                    </span>
                  )}
                </GameClubImage>
              </GameClubImageUpload>
              <GameClubJoinName>
                {type === "join" || type === "read" ? (
                  clubName
                ) : (
                  <input
                    type="text"
                    value={clubName}
                    onChange={(e) => setClubName(e.target.value)}
                    placeholder="클럽 이름 입력"
                  />
                )}
              </GameClubJoinName>
            </div>
            {type === "join" || type === "read" ? (
              <div className="club-info">
                <div className="content">{clubInfo}</div>
              </div>
            ) : (
              <textarea
                value={clubInfo}
                onChange={(e) => setClubInfo(e.target.value)}
                placeholder="클럽 설명을 입력해주세요(최대 512자)"
              />
            )}
          </GameClubJoinContent>
          {type !== "read" && (
            <>
              {type === "join" && (
                <GameClubJoinWarning>
                  {warningText.map((item, index) => (
                    <div className="item" key={index}>
                      <img
                        src={`/image-web/game/club/checked${
                          !item.isRead ? "-gray" : ""
                        }.svg`}
                        alt="check"
                        onClick={() => {
                          const updateText = [...warningText];
                          updateText[index] = {
                            ...updateText[index],
                            isRead: !warningText[index].isRead,
                          };
                          setWarningText(updateText);
                        }}
                      />
                      <div>{item.text}</div>
                    </div>
                  ))}
                </GameClubJoinWarning>
              )}

              <div
                className={`button ${type}`}
                onClick={() => {
                  if (type === "join") {
                    handleJoin();
                  } else if (type === "edit") {
                    handleEdit();
                  } else if (type === "opened") {
                    handleCreate();
                  }
                }}
              >
                {getButtonText()}
              </div>
            </>
          )}
          {isSelectProfile && (
            <GameClubImageSelectWrapper>
              <GameClubImageSelectItem
                isSelected={selectedProfileType === 0}
                onClick={() => setSelectedProfileType(0)}
              >
                <img
                  src="/image-web/game/club/profile_0.png"
                  alt="프로필 이미지"
                />
                <span>유튜버 클럽</span>
              </GameClubImageSelectItem>
              <GameClubImageSelectItem
                isSelected={selectedProfileType === 1}
                onClick={() => setSelectedProfileType(1)}
              >
                <img
                  src="/image-web/game/club/profile_1.png"
                  alt="프로필 이미지"
                />
                <span>러너펍 클럽</span>
              </GameClubImageSelectItem>
              <GameClubImageSelectItem
                isSelected={selectedProfileType === 2}
                onClick={() => setSelectedProfileType(2)}
              >
                <img
                  src="/image-web/game/club/profile_2.png"
                  alt="프로필 이미지"
                />
                <span>커뮤니티 클럽</span>
              </GameClubImageSelectItem>
              <GameClubImageSelectItem
                isSelected={selectedProfileType === 3}
                onClick={() => setSelectedProfileType(3)}
              >
                <img
                  src="/image-web/game/club/profile_3.png"
                  alt="프로필 이미지"
                />
                <span>개인 클럽</span>
              </GameClubImageSelectItem>
            </GameClubImageSelectWrapper>
          )}
        </GameClubJoinContentWrapper>
      </GameClubJoinWrapper>
    </>
  );
};
